import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Checkbox } from '../ui/checkbox';
import { gstRates, states, hsnCodes, calculateTax } from '../../data/mockData';
import { Calculator, AlertCircle, Save, X } from 'lucide-react';

const GSTR1AddRecord = ({ tileId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    gstin: '',
    invoiceNo: '',
    invoiceDate: '',
    taxableValue: '',
    rate: '',
    igst: '0.00',
    cgst: '0.00',
    sgst: '0.00',
    placeOfSupply: '',
    isInterState: true,
    hsnCode: '',
    description: '',
    quantity: '',
    unit: 'NOS'
  });

  const [errors, setErrors] = useState({});

  const handleTaxableValueChange = (value) => {
    setFormData(prev => {
      const newData = { ...prev, taxableValue: value };
      if (value && prev.rate) {
        const taxes = calculateTax(value, prev.rate, prev.isInterState);
        newData.igst = taxes.igst;
        newData.cgst = taxes.cgst;
        newData.sgst = taxes.sgst;
      }
      return newData;
    });
  };

  const handleRateChange = (rate) => {
    setFormData(prev => {
      const newData = { ...prev, rate };
      if (prev.taxableValue && rate) {
        const taxes = calculateTax(prev.taxableValue, rate, prev.isInterState);
        newData.igst = taxes.igst;
        newData.cgst = taxes.cgst;
        newData.sgst = taxes.sgst;
      }
      return newData;
    });
  };

  const handleInterStateChange = (checked) => {
    setFormData(prev => {
      const newData = { ...prev, isInterState: checked };
      if (prev.taxableValue && prev.rate) {
        const taxes = calculateTax(prev.taxableValue, prev.rate, checked);
        newData.igst = taxes.igst;
        newData.cgst = taxes.cgst;
        newData.sgst = taxes.sgst;
      }
      return newData;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (tileId === '4A' || tileId === '9B' || tileId === '10') {
      if (!formData.gstin || !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)) {
        newErrors.gstin = 'Valid GSTIN required (e.g., 07AAACG1234A1ZM)';
      }
    }

    if (!formData.invoiceNo) newErrors.invoiceNo = 'Invoice number is required';
    if (!formData.invoiceDate) newErrors.invoiceDate = 'Invoice date is required';
    if (!formData.taxableValue || parseFloat(formData.taxableValue) <= 0) {
      newErrors.taxableValue = 'Valid taxable value is required';
    }
    if (!formData.rate && formData.rate !== 0) newErrors.rate = 'Tax rate is required';
    if (!formData.placeOfSupply) newErrors.placeOfSupply = 'Place of supply is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  const showGSTIN = ['4A', '9B', '10'].includes(tileId);
  const showHSN = ['12'].includes(tileId);

  return (
    <div className="space-y-6 py-4">
      <Alert className="bg-blue-50 border-blue-200">
        <Calculator className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Tax is auto-calculated when you enter Taxable Value and select Rate. Toggle Inter-State for IGST or Intra-State for CGST+SGST.
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-2 gap-6">
        {/* GSTIN Field (for B2B) */}
        {showGSTIN && (
          <div className="space-y-2">
            <Label>GSTIN of Recipient <span className="text-red-500">*</span></Label>
            <Input
              value={formData.gstin}
              onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
              placeholder="e.g., 07AAACG1234A1ZM"
              maxLength={15}
              className={errors.gstin ? 'border-red-500' : ''}
            />
            {errors.gstin && <p className="text-sm text-red-500">{errors.gstin}</p>}
          </div>
        )}

        {/* Invoice Number */}
        <div className="space-y-2">
          <Label>Invoice Number <span className="text-red-500">*</span></Label>
          <Input
            value={formData.invoiceNo}
            onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value.toUpperCase() })}
            placeholder="e.g., INV/2024/001"
            className={errors.invoiceNo ? 'border-red-500' : ''}
          />
          {errors.invoiceNo && <p className="text-sm text-red-500">{errors.invoiceNo}</p>}
        </div>

        {/* Invoice Date */}
        <div className="space-y-2">
          <Label>Invoice Date <span className="text-red-500">*</span></Label>
          <Input
            type="date"
            value={formData.invoiceDate}
            onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
            className={errors.invoiceDate ? 'border-red-500' : ''}
          />
          {errors.invoiceDate && <p className="text-sm text-red-500">{errors.invoiceDate}</p>}
        </div>

        {/* Place of Supply */}
        <div className="space-y-2">
          <Label>Place of Supply <span className="text-red-500">*</span></Label>
          <Select
            value={formData.placeOfSupply}
            onValueChange={(value) => setFormData({ ...formData, placeOfSupply: value })}
          >
            <SelectTrigger className={errors.placeOfSupply ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select State" />
            </SelectTrigger>
            <SelectContent>
              {states.map(state => (
                <SelectItem key={state.code} value={state.code}>
                  {state.code} - {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.placeOfSupply && <p className="text-sm text-red-500">{errors.placeOfSupply}</p>}
        </div>

        {/* HSN Code (for HSN Summary) */}
        {showHSN && (
          <div className="space-y-2">
            <Label>HSN Code <span className="text-red-500">*</span></Label>
            <Select
              value={formData.hsnCode}
              onValueChange={(value) => setFormData({ ...formData, hsnCode: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select HSN Code" />
              </SelectTrigger>
              <SelectContent>
                {hsnCodes.map(hsn => (
                  <SelectItem key={hsn.code} value={hsn.code}>
                    {hsn.code} - {hsn.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Taxable Value */}
        <div className="space-y-2">
          <Label>Taxable Value (₹) <span className="text-red-500">*</span></Label>
          <Input
            type="number"
            value={formData.taxableValue}
            onChange={(e) => handleTaxableValueChange(e.target.value)}
            placeholder="Enter taxable amount"
            className={errors.taxableValue ? 'border-red-500' : ''}
          />
          {errors.taxableValue && <p className="text-sm text-red-500">{errors.taxableValue}</p>}
        </div>

        {/* Tax Rate */}
        <div className="space-y-2">
          <Label>Tax Rate <span className="text-red-500">*</span></Label>
          <Select
            value={formData.rate?.toString()}
            onValueChange={(value) => handleRateChange(value)}
          >
            <SelectTrigger className={errors.rate ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select Rate" />
            </SelectTrigger>
            <SelectContent>
              {gstRates.map(rate => (
                <SelectItem key={rate.rate} value={rate.rate.toString()}>
                  {rate.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.rate && <p className="text-sm text-red-500">{errors.rate}</p>}
        </div>
      </div>

      {/* Inter/Intra State Toggle */}
      <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
        <Checkbox
          id="interstate"
          checked={formData.isInterState}
          onCheckedChange={handleInterStateChange}
        />
        <Label htmlFor="interstate" className="text-sm cursor-pointer">
          Inter-State Supply (Different State - IGST applicable)
        </Label>
      </div>

      {/* Auto-calculated Tax Display */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">IGST</p>
          <p className="text-lg font-bold text-green-700">₹{formData.igst}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">CGST</p>
          <p className="text-lg font-bold text-green-700">₹{formData.cgst}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">SGST/UTGST</p>
          <p className="text-lg font-bold text-green-700">₹{formData.sgst}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="bg-[#0D1B2A] hover:bg-[#1B2838]">
          <Save className="w-4 h-4 mr-2" />
          Save Record
        </Button>
      </div>
    </div>
  );
};

export default GSTR1AddRecord;
